import { useCallback, useMemo, useReducer, useRef } from "react";
import produce from "immer";

import { RPCRequests } from "src/modules/communication-protobuf";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import debug from "src/modules/debug-logger";
import { getDismissedAfterFirstMediaCaptureDialog } from "src/modules/activity-log/duck/selectors";
import { store } from "src/store/store";
import { withMessenger } from "src/modules/messenger/Provider";
import { downloadEventMedia } from "src/modules/activity-log/ajax/cloud";

import CapturedMedia from "../../components/CapturedMedia";
import AfterFirstMediaCaptureDialog from "./AfterFirstMediaCaptureDialog";
import SnapshotAnimation from "./SnapshotAnimation";
import RecordingInProgressView from "./RecordingInProgressView";
import useCamera from "./useCamera";
import useRecorder from "./useRecorder";
import { withParentStation } from "src/modules/parent-station/ParentStation/Context";

const initialState = {
  camera: {
    status: "initial" // ArrayOfString[initial, waitingForResponse]
  },
  recorder: {
    status: "initial", // ArrayOfString[initial, waitingForResponse, recording, waitingForThumbnail, finished]
    recordingLimit: 10000,
    recordingStartTime: null
  },
  capturedEvent: null,
  hasGenericFailure: false,
  isOpenAfterFirstMediaCaptureDialog: false,
  isDeleting: false
};

const recuder = produce((state, action) => {
  switch (action.type) {
    case "START_RECORDING_REQUEST":
      return {
        ...initialState,
        recorder: { ...initialState.recorder, status: "waitingForResponse" }
      };
    case "START_RECORDING_SUCCESS":
      state.recorder.status = "recording";
      state.recorder.recordingLimit = action.payload.recordingLimit;
      state.recorder.recordingStartTime = Date.now();
      return;
    case "RECORDING_DID_FINISH":
      state.recorder.status = "finished";
      state.capturedEvent = action.payload.capturedEvent;
      return;
    case "STOP_RECORDING_REQUEST":
    case "REACHED_RECORDING_LIMIT":
      state.recorder.status = "waitingForThumbnail";
      return;
    case "GET_VIDEO_SRC_SUCCESS":
      state.capturedEvent.signedUrl = action.payload.signedUrl;
      return;
    case "START_RECORDING_FAILURE":
    case "STOP_RECORDING_FAILURE":
    case "RECORDING_FAILED":
    case "TAKE_VIDEO_SNAPSHOT_FAILURE":
    case "DELETE_EVENT_FAILURE":
      return { ...initialState, hasGenericFailure: true };
    case "TAKE_VIDEO_SNAPSHOT_REQUEST":
      return {
        ...initialState,
        camera: { ...initialState.camera, status: "waitingForResponse" }
      };
    case "TAKE_VIDEO_SNAPSHOT_SUCCESS":
      state.capturedEvent = action.payload;
      state.camera.status = "initial";
      return;
    case "DELETE_EVENT_REQUEST":
      state.isDeleting = true;
      return;
    case "CLOSE_MEDIA_VIEW":
      return {
        ...initialState,
        isOpenAfterFirstMediaCaptureDialog:
          !getDismissedAfterFirstMediaCaptureDialog(store.getState())
      };
    case "DELETE_EVENT_SUCCESS":
    case "DISMISS_GENERIC_FAILURE_DIALOG":
    case "DISMISS_AFTER_FIRST_MEDIA_CAPTURE_DIALOG":
      return initialState;
    default:
      return;
  }
});

export default function withMediaCapturer(Component) {
  function MediaCapturer(props) {
    const { messenger, parentStation } = props;
    const { primaryStation } = parentStation;
    const { rpc, remoteVideoStream, monitoringStartTime, videoMode } =
      primaryStation;
    const [state, dispatch] = useReducer(recuder, initialState);
    const latestState = useRef(state);
    latestState.current = state;

    const camera = useCamera({
      dispatch,
      latestState,
      rpc,
      peerJid: primaryStation.jid
    });
    const recorder = useRecorder({
      dispatch,
      latestState,
      rpc,
      messenger,
      peerJid: primaryStation.jid
    });

    const deleteEvent = useCallback(async () => {
      if (latestState.current.isDeleting) return;
      dispatch({ type: "DELETE_EVENT_REQUEST" });
      const request = RPCRequests.DeleteActivityLogEvent.create(
        state.capturedEvent.id
      );
      try {
        await rpc.call(request, primaryStation.jid);
        dispatch({ type: "DELETE_EVENT_SUCCESS" });
      } catch (error) {
        debug.activityLog("deleteEvent failed:", error);
        dispatch({ type: "DELETE_EVENT_FAILURE" });
      }
    }, [rpc, primaryStation.jid, state.capturedEvent, latestState]);

    const handleEventMediaDownload = useCallback(async () => {
      try {
        await downloadEventMedia({
          eventType: state.capturedEvent.eventType,
          fileName: state.capturedEvent.fileName.value,
          data: state.capturedEvent.data,
          captureDate:
            new Date(monitoringStartTime).getTime() + state.capturedEvent.start
        });
      } catch (error) {
        debug.activityLog("downloadEventMedia failed: ", error);
      }
    }, [state.capturedEvent, monitoringStartTime]);

    const mediaCapturer = useMemo(
      () => ({
        state,
        takeSnapshot: camera.takeSnapshot,
        startRecording: recorder.startRecording,
        stopRecording: recorder.stopRecording,
        getRecordingProgress: recorder.getRecordingProgress,
        onRecordingLimitReached: recorder.onRecordingLimitReached
      }),
      [camera, recorder, state]
    );

    return (
      <>
        <Component mediaCapturer={mediaCapturer} {...props} />
        <CapturedMedia
          isOpen={Boolean(state.capturedEvent)}
          onClose={() => {
            dispatch({ type: "CLOSE_MEDIA_VIEW" });
          }}
          type={state.capturedEvent?.eventType}
          src={
            state.capturedEvent?.signedUrl || state.capturedEvent?.mediaDataUrl
          }
          videoThumbnailSrc={state.capturedEvent?.videoThumbnailSrc}
          ratio={state.capturedEvent?.mediaRatio}
          onDelete={deleteEvent}
          onDownload={handleEventMediaDownload}
          autoplay={false}
        />
        <GenericFailureDialog
          isOpen={state.hasGenericFailure}
          onDismiss={() => dispatch({ type: "DISMISS_GENERIC_FAILURE_DIALOG" })}
        />
        <AfterFirstMediaCaptureDialog
          isOpen={state.isOpenAfterFirstMediaCaptureDialog}
          onDismiss={() =>
            dispatch({ type: "DISMISS_AFTER_FIRST_MEDIA_CAPTURE_DIALOG" })
          }
        />
        <SnapshotAnimation in={state.camera.status === "waitingForResponse"} />
        <RecordingInProgressView
          mediaCapturer={mediaCapturer}
          videoStream={remoteVideoStream}
          videoMode={videoMode}
        />
      </>
    );
  }

  return withMessenger(withParentStation(MediaCapturer));
}
